import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { AutoComplete, Button, Input, Pagination, Select, Tag, message } from 'antd'
import { DeleteOutlined, FileSearchOutlined, SettingOutlined } from '@ant-design/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../api'
import { KNOWLEDGE_SCOPE_META, KNOWLEDGE_SCOPE_OPTIONS, type KnowledgeScope } from '../constants/knowledgeScope'
import { listPagination, PageHeader, PageLoading, useServerPagination } from '../components/ui'
import { VectorSearchChunkDrawer } from '../components/VectorSearchChunkDrawer'
import {
  formatAttrKv,
  parseAttributesFromSearchParams,
  type SelectedAttribute,
} from '../lib/attributeSearch'
import { resolveSearchHighlight, highlightSearchQuery } from '../lib/searchHighlight'
import { clearSearchHistory, loadSearchHistory, pushSearchHistory } from '../lib/searchHistory'
import { openKnowledgeDocPreview } from '../lib/knowledgeDocPreview'
import './Search.css'

type SearchMode = 'hybrid' | 'keyword' | 'vector' | 'attr'

type Hit = {
  item_id: number
  chunk_index: number
  title: string
  category: string
  snippet: string
  snippet_html?: string
  title_html?: string
  score: number
  match_count?: number
}

type SearchRes = {
  query: string
  hits: Hit[]
  total: number
  page: number
  page_size: number
  response_ms: number
  mode?: SearchMode | 'text'
  engine?: string
  /** 向量模式：hits 为完整榜单，翻页仅前台切片 */
  client_paginate?: boolean
}

type AttrSearchItem = {
  item_id: number
  title: string
  matched_attributes: string[]
  matched_count: number
  required_count: number
}

type AttrSearchRes = {
  scope: KnowledgeScope
  attributes: SelectedAttribute[]
  match_mode: 'all'
  items: AttrSearchItem[]
  total: number
  page: number
  page_size: number
  response_ms: number
}

type SearchTab = 'ai' | 'doc' | 'policy' | 'process' | 'faq'

type SuggestRes = {
  query: string
  suggestions: { value: string }[]
}

type SuggestOption = { value: string; label: ReactNode }

const SEARCH_TABS: { key: SearchTab; label: string; match?: (h: Hit) => boolean }[] = [
  { key: 'ai', label: 'AI搜索' },
  {
    key: 'doc',
    label: '文档',
    match: (h) => /文档|资料|手册/i.test(`${h.category} ${h.title}`),
  },
  {
    key: 'policy',
    label: '制度',
    match: (h) => /制度|规范|政策|办法/i.test(`${h.category} ${h.title}`),
  },
  {
    key: 'process',
    label: '流程',
    match: (h) => /流程|审批|操作/i.test(`${h.category} ${h.title}`),
  },
  {
    key: 'faq',
    label: '问答',
    match: (h) => /问答|FAQ|常见/i.test(`${h.category} ${h.title}`),
  },
]

const MODE_OPTIONS: { value: SearchMode; label: string }[] = [
  { value: 'hybrid', label: '混合' },
  { value: 'vector', label: '向量' },
  { value: 'keyword', label: '关键词' },
  { value: 'attr', label: '属性' },
]

const MODE_HINTS: Record<SearchMode, string> = {
  keyword: '关键词模式：IK 分词字面匹配，支持高亮',
  hybrid: '混合模式：BM25 全文与向量语义，按文档聚合',
  vector: '向量模式：语义近邻召回，按文档聚合',
  attr: '属性模式：档案关键信息的关键词 + 向量混合，不查正文切片',
}

const SCOPE_OPTIONS = KNOWLEDGE_SCOPE_OPTIONS

function HtmlSnippet({ html, className }: { html: string; className?: string }) {
  return <span className={className} dangerouslySetInnerHTML={{ __html: html }} />
}

function suggestOptionLabel(value: string, query: string): ReactNode {
  const html = highlightSearchQuery(value, query)
  if (html) {
    return <span className="search-suggest-option" dangerouslySetInnerHTML={{ __html: html }} />
  }
  return value
}

const HISTORY_PANEL_OPTION = '__history_panel__'

export default function Search() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const urlAttr = useMemo(() => parseAttributesFromSearchParams(searchParams), [searchParams])
  const [q, setQ] = useState('')
  const [scope, setScope] = useState<KnowledgeScope>(() => {
    const s = searchParams.get('scope')
    return s === 'personal' || s === 'enterprise' ? s : 'enterprise'
  })
  const [datasetId, setDatasetId] = useState<number | undefined>(undefined)
  const [datasetOptions, setDatasetOptions] = useState<{ value: number; label: string }[]>([])
  const [mode, setMode] = useState<SearchMode>('keyword')
  const [tab, setTab] = useState<SearchTab>('ai')
  const { page, pageSize, setPage, handlePaginationChange, paginationOptions } =
    useServerPagination()
  const [loading, setLoading] = useState(false)
  const [res, setRes] = useState<SearchRes | null>(null)
  const [attrRes, setAttrRes] = useState<AttrSearchRes | null>(null)
  const [attrSearch, setAttrSearch] = useState(urlAttr.attrSearch)
  const [attrCriteria, setAttrCriteria] = useState<SelectedAttribute[]>(urlAttr.attributes)
  const [attrFromItemId, setAttrFromItemId] = useState(urlAttr.fromItemId)
  const [vectorDrawerOpen, setVectorDrawerOpen] = useState(false)
  const [vectorDrawerHit, setVectorDrawerHit] = useState<Hit | null>(null)
  const [suggestOptions, setSuggestOptions] = useState<SuggestOption[]>([])
  const [inputFocused, setInputFocused] = useState(false)
  const [historyTick, setHistoryTick] = useState(0)
  const resultsRef = useRef<HTMLElement | null>(null)
  const searchPillRef = useRef<HTMLFormElement>(null)

  const searchHistory = useMemo(
    () => loadSearchHistory(scope),
    [scope, historyTick],
  )

  useEffect(() => {
    api
      .get<{ items: { id: number; name: string }[] }>('/public/datasets/options', {
        params: { scope },
      })
      .then((r) =>
        setDatasetOptions((r.data.items || []).map((d) => ({ value: d.id, label: d.name }))),
      )
      .catch(() => setDatasetOptions([]))
  }, [scope])

  const showHistoryPanel = inputFocused && !q.trim()
  const showSuggestPanel = inputFocused && !!q.trim() && suggestOptions.length > 0
  const dropdownOpen = showHistoryPanel || showSuggestPanel

  const autocompleteOptions = q.trim()
    ? suggestOptions
    : showHistoryPanel
      ? [{ value: HISTORY_PANEL_OPTION, label: ' ' }]
      : []

  useEffect(() => {
    const text = q.trim()
    if (text.length < 1) {
      setSuggestOptions([])
      return
    }
    const timer = window.setTimeout(() => {
      api
        .get<SuggestRes>('/public/search/suggest', { params: { q: text, scope, limit: 10 } })
        .then((r) => {
          setSuggestOptions(
            (r.data.suggestions || []).map((s) => ({
              value: s.value,
              label: suggestOptionLabel(s.value, text),
            })),
          )
        })
        .catch(() => setSuggestOptions([]))
    }, 300)
    return () => window.clearTimeout(timer)
  }, [q, scope])

  const runAttrSearch = useCallback(
    (attrs: SelectedAttribute[], pageNum: number, nextScope = scope, fromItem = attrFromItemId, ps = pageSize) => {
      if (!attrs.length) return
      setLoading(true)
      setAttrSearch(true)
      setAttrCriteria(attrs)
      setRes(null)
      api
        .post<AttrSearchRes>('/public/search/by-attributes', {
          scope: nextScope,
          attributes: attrs,
          exclude_item_id: fromItem > 0 ? fromItem : undefined,
          page: pageNum,
          page_size: ps,
        })
        .then((r) => {
          setAttrRes(r.data)
          setPage(r.data.page ?? pageNum)
          setTab('ai')
          resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
        .catch((e) => message.error(e.message))
        .finally(() => setLoading(false))
    },
    [scope, attrFromItemId, pageSize],
  )

  useEffect(() => {
    const parsed = parseAttributesFromSearchParams(searchParams)
    if (parsed.attrSearch && parsed.attributes.length > 0) {
      const nextScope =
        searchParams.get('scope') === 'personal' || searchParams.get('scope') === 'enterprise'
          ? (searchParams.get('scope') as KnowledgeScope)
          : scope
      if (nextScope !== scope) setScope(nextScope)
      setAttrFromItemId(parsed.fromItemId)
      setAttrCriteria(parsed.attributes)
      setAttrSearch(true)
      setPage(1)
      runAttrSearch(parsed.attributes, 1, nextScope, parsed.fromItemId)
    }
    // 仅 URL 进入时触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const runSearch = (query: string, pageNum: number, ps = pageSize) => {
    const text = query.trim()
    if (!text) return
    setLoading(true)
    setAttrSearch(false)
    setAttrRes(null)
    api
      .post<SearchRes>('/public/search', {
        query: text,
        page: pageNum,
        page_size: ps,
        scope,
        mode,
        ...(datasetId ? { dataset_id: datasetId } : {}),
      })
      .then((r) => {
        setRes(r.data)
        setPage(r.data.page ?? pageNum)
        pushSearchHistory(scope, text)
        setHistoryTick((n) => n + 1)
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
      .catch((e) => message.error(e.message))
      .finally(() => setLoading(false))
  }

  const onSearch = (query?: string) => {
    const text = (query ?? q).trim()
    if (!text) return
    if (query) setQ(query)
    setPage(1)
    runSearch(text, 1)
  }

  const pickHistory = (text: string) => {
    setInputFocused(false)
    onSearch(text)
  }

  const historyDropdown = (
    <div className="search-dropdown-panel" onMouseDown={(e) => e.preventDefault()}>
      {searchHistory.length > 0 ? (
        <section className="search-dropdown-block">
          <div className="search-dropdown-head">
            <span>历史搜索</span>
            <Button
              type="text"
              size="small"
              className="search-dropdown-icon-btn"
              icon={<DeleteOutlined />}
              aria-label="清空历史搜索"
              onClick={() => {
                clearSearchHistory(scope)
                setHistoryTick((n) => n + 1)
              }}
            />
          </div>
          <div className="search-dropdown-tags">
            {searchHistory.map((h) => (
              <button
                key={h}
                type="button"
                className="search-dropdown-tag"
                title={h}
                onClick={() => pickHistory(h)}
              >
                {h}
              </button>
            ))}
          </div>
        </section>
      ) : (
        <section className="search-dropdown-block">
          <div className="search-dropdown-head">
            <span>历史搜索</span>
          </div>
          <p className="search-dropdown-empty">暂无搜索历史，搜索后会出现在这里</p>
        </section>
      )}
      <section className="search-dropdown-block">
        <div className="search-dropdown-head">
          <span>常用筛选</span>
          <Button
            type="text"
            size="small"
            className="search-dropdown-icon-btn"
            icon={<SettingOutlined />}
            aria-label="筛选设置"
            disabled
          />
        </div>
        <div className="search-dropdown-tags">
          {SEARCH_TABS.filter((t) => t.key !== 'ai').map((t) => (
            <button
              key={t.key}
              type="button"
              className={`search-dropdown-tag${tab === t.key ? ' search-dropdown-tag-active' : ''}`}
              onClick={() => {
                setTab(t.key)
                setInputFocused(false)
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>
    </div>
  )

  const filteredHits = useMemo(() => {
    if (!res) return []
    const def = SEARCH_TABS.find((t) => t.key === tab)
    if (!def?.match) return res.hits
    return res.hits.filter(def.match)
  }, [res, tab])

  const visibleHits = useMemo(() => {
    if (!res?.client_paginate) return filteredHits
    const off = (page - 1) * pageSize
    return filteredHits.slice(off, off + pageSize)
  }, [res, filteredHits, page, pageSize])

  const modeLabel = MODE_OPTIONS.find((o) => o.value === mode)?.label ?? mode
  const scoreLabel = mode === 'vector' ? '相似度' : '相关度'
  const resultTotal = res?.client_paginate ? filteredHits.length : (res?.total ?? 0)
  const resultUnit = '篇'
  const showPagination =
    tab === 'ai' &&
    (resultTotal > pageSize || page > 1 || visibleHits.length >= pageSize)

  const openVectorChunks = (hit: Hit) => {
    if (mode !== 'vector' || !res?.query) return
    setVectorDrawerHit(hit)
    setVectorDrawerOpen(true)
  }

  const openDocPreview = useCallback(
    (hit: Hit) => {
      openKnowledgeDocPreview(hit.item_id, scope, {
        query: (res?.query ?? q).trim(),
        onNeedLogin: () => {
          message.warning('预览文档需使用管理账号登录（与后台同一账号）')
          void navigate(`/manage/login?redirect=${encodeURIComponent('/search')}`)
        },
      })
    },
    [navigate, scope, res?.query, q],
  )

  const openAttrDocPreview = useCallback(
    (hit: AttrSearchItem) => {
      openKnowledgeDocPreview(hit.item_id, scope, {
        onNeedLogin: () => {
          message.warning('预览文档需使用管理账号登录（与后台同一账号）')
          void navigate(`/manage/login?redirect=${encodeURIComponent('/search')}`)
        },
      })
    },
    [navigate, scope],
  )

  const attrResultTotal = attrRes?.total ?? 0
  const attrVisibleItems = attrRes?.items ?? []
  const showAttrPagination =
    attrSearch && (attrResultTotal > pageSize || page > 1 || attrVisibleItems.length >= pageSize)

  const onHitCardClick = (hit: Hit) => {
    if (mode === 'vector') openVectorChunks(hit)
    else openDocPreview(hit)
  }

  return (
    <div className="search-page">
      <section className="search-hero">
        <div className="search-hero-bg" aria-hidden />
        <div className="search-hero-inner">
          <PageHeader
            level={2}
            className="search-hero-header"
            title="智能搜索与问答"
            description={
              attrSearch
                ? `检索范围：${KNOWLEDGE_SCOPE_META[scope].searchHint}；同属性文档（须同时满足所选属性）`
                : `检索范围：${KNOWLEDGE_SCOPE_META[scope].searchHint}；${MODE_HINTS[mode]}`
            }
          />

          <nav className="search-cats" role="tablist" aria-label="检索分类">
            {SEARCH_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={`search-cat${tab === t.key ? ' search-cat-active' : ''}`}
                onClick={() => {
                  if (t.key === tab) return
                  setTab(t.key)
                  setPage(1)
                  if (t.key !== 'ai' && res?.query && !res?.client_paginate && page !== 1) {
                    runSearch(res.query, 1)
                  }
                }}
              >
                {t.label}
              </button>
            ))}
          </nav>

          <form
            ref={searchPillRef}
            className="search-pill"
            onSubmit={(e) => {
              e.preventDefault()
              onSearch()
            }}
          >
            <Select
              className="search-pill-mode"
              value={scope}
              options={SCOPE_OPTIONS}
              onChange={(next) => {
                setScope(next)
                setDatasetId(undefined)
                setRes(null)
                setPage(1)
              }}
              variant="borderless"
              popupMatchSelectWidth={false}
            />
            <Select
              className="search-pill-mode"
              value={datasetId ?? 0}
              options={[{ value: 0, label: '全部文档' }, ...datasetOptions]}
              onChange={(v) => {
                setDatasetId(v === 0 ? undefined : v)
                setRes(null)
                setPage(1)
              }}
              variant="borderless"
              popupMatchSelectWidth={false}
            />
            <Select
              className="search-pill-mode"
              value={mode}
              options={MODE_OPTIONS}
              disabled={attrSearch}
              onChange={(next) => {
                setMode(next)
                setRes(null)
                setAttrRes(null)
                setAttrSearch(false)
                setPage(1)
              }}
              variant="borderless"
              popupMatchSelectWidth={false}
            />
            <AutoComplete
              className="search-pill-input"
              value={q}
              options={autocompleteOptions}
              popupClassName={`search-suggest-popup${showHistoryPanel ? ' search-suggest-popup--history' : ''}`}
              popupMatchSelectWidth={!showHistoryPanel}
              getPopupContainer={() => searchPillRef.current ?? document.body}
              open={dropdownOpen}
              dropdownRender={(menu) => (q.trim() ? menu : historyDropdown)}
              onOpenChange={(open) => {
                if (!open) setInputFocused(false)
              }}
              onFocus={() => setInputFocused(true)}
              onBlur={() => {
                window.setTimeout(() => setInputFocused(false), 200)
              }}
              onChange={setQ}
              onSelect={(val) => {
                const text = String(val)
                if (text === HISTORY_PANEL_OPTION) return
                setInputFocused(false)
                onSearch(text)
              }}
              defaultActiveFirstOption={!!q.trim()}
            >
              <Input
                variant="borderless"
                placeholder={
                  mode === 'keyword'
                    ? '输入关键词，空格分隔多词'
                    : mode === 'attr'
                      ? '输入属性名或属性值，如客户名称、转账'
                      : '输入自然语言问题或描述'
                }
              />
            </AutoComplete>
            <Button
              type="primary"
              htmlType="submit"
              className="search-pill-btn"
              loading={loading}
            >
              搜索
            </Button>
          </form>
        </div>
      </section>

      <div className="search-body">
        {attrSearch && attrCriteria.length > 0 ? (
          <div className="search-attr-criteria-bar">
            <span className="search-attr-criteria-label">查找同属性文档（同时满足）：</span>
            {attrCriteria.map((a) => (
              <Tag key={formatAttrKv(a)} color="gold">
                {formatAttrKv(a)}
              </Tag>
            ))}
            <Button
              type="link"
              size="small"
              onClick={() => {
                setAttrSearch(false)
                setAttrRes(null)
                setAttrCriteria([])
                setPage(1)
                void navigate('/search', { replace: true })
              }}
            >
              返回关键词搜索
            </Button>
          </div>
        ) : null}

        {loading && (
          <div className="search-body-loading">
            <PageLoading />
          </div>
        )}

        {!loading && attrSearch && attrRes && (attrResultTotal > 0 || attrVisibleItems.length > 0) && (
          <section ref={resultsRef} className="search-results-panel">
            <div className="search-meta-bar">
              <Tag color="blue">共 {attrResultTotal} 篇</Tag>
              {attrResultTotal > pageSize ? (
                <span>
                  第 {page} / {Math.max(1, Math.ceil(attrResultTotal / pageSize))} 页
                </span>
              ) : null}
              <span>耗时 {attrRes.response_ms} ms</span>
              <Tag color="gold">同属性 AND</Tag>
            </div>
            {attrVisibleItems.map((h) => (
              <article
                key={h.item_id}
                className="search-hit-card search-hit-card--clickable"
                role="button"
                tabIndex={0}
                onClick={() => openAttrDocPreview(h)}
                onKeyDown={(e: KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    openAttrDocPreview(h)
                  }
                }}
              >
                <div className="search-hit-title">
                  <FileSearchOutlined style={{ color: 'var(--primary)' }} />
                  <span>{h.title}</span>
                  <Tag>ID {h.item_id}</Tag>
                  <Tag color="processing">
                    匹配 {h.matched_count}/{h.required_count}
                  </Tag>
                </div>
                <p className="search-hit-snippet search-hit-attr-match">
                  {h.matched_attributes.map((kv) => (
                    <Tag key={kv} color="gold" className="search-hit-attr-tag">
                      {kv}
                    </Tag>
                  ))}
                </p>
              </article>
            ))}
            {showAttrPagination ? (
              <div className="search-results-pagination">
                <Pagination
                  {...listPagination(page, attrResultTotal, (p, ps) => {
                    const nextSize = ps ?? pageSize
                    const nextPage = nextSize !== pageSize ? 1 : p
                    handlePaginationChange(p, ps)
                    runAttrSearch(attrCriteria, nextPage, scope, attrFromItemId, nextSize)
                  }, { unit: '篇', ...paginationOptions })}
                />
              </div>
            ) : null}
          </section>
        )}

        {!loading && attrSearch && attrRes && attrResultTotal === 0 && (
          <div className="search-empty-inline">
            没有同时满足所选属性的文档，请减少已选属性后重试
          </div>
        )}

        {!loading && !attrSearch && res && (resultTotal > 0 || visibleHits.length > 0) && (
          <section ref={resultsRef} className="search-results-panel">
            <div className="search-meta-bar">
              <Tag color="blue">
                共 {resultTotal} {resultUnit}
                {tab !== 'ai' && filteredHits.length !== res.hits.length
                  ? `，当前分类 ${filteredHits.length} 条`
                  : null}
              </Tag>
              {resultTotal > pageSize ? (
                <span>
                  第 {page} / {Math.max(1, Math.ceil(resultTotal / pageSize))} 页
                </span>
              ) : null}
              <span>耗时 {res.response_ms} ms</span>
              <span>查询：{res.query}</span>
              <Tag>{modeLabel}</Tag>
              {tab !== 'ai' && <Tag>{SEARCH_TABS.find((t) => t.key === tab)?.label}</Tag>}
            </div>
            {visibleHits.map((h) => {
              const q = res?.query ?? ''
              const titleHtml = resolveSearchHighlight(h.title, q, h.title_html)
              const snippetHtml = resolveSearchHighlight(h.snippet, q, h.snippet_html)
              return (
              <article
                key={`${h.item_id}-${h.chunk_index}-${mode}`}
                className="search-hit-card search-hit-card--clickable"
                role="button"
                tabIndex={0}
                onClick={() => onHitCardClick(h)}
                onKeyDown={(e: KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onHitCardClick(h)
                  }
                }}
              >
                <div className="search-hit-title">
                  <FileSearchOutlined style={{ color: 'var(--primary)' }} />
                  {titleHtml ? (
                    <HtmlSnippet html={titleHtml} />
                  ) : (
                    <span>{h.title}</span>
                  )}
                  <Tag>ID {h.item_id}</Tag>
                  {mode === 'attr' && (h.match_count ?? 1) > 0 ? (
                    <Tag>{h.match_count} 条属性</Tag>
                  ) : null}
                  {mode !== 'vector' && mode !== 'attr' && (h.match_count ?? 1) > 1 ? (
                    <Tag>{h.match_count} 处相关</Tag>
                  ) : null}
                  {mode === 'vector' && (h.match_count ?? 1) > 1 ? (
                    <Tag>{h.match_count} 段相似</Tag>
                  ) : null}
                  {h.category ? <Tag>{h.category}</Tag> : null}
                  <Tag color="processing">{scoreLabel} {h.score.toFixed(2)}</Tag>
                </div>
                {mode === 'vector' ? (
                  <p className="search-hit-snippet">
                    {h.snippet ? `最相似片段：${h.snippet}` : '（无摘要）'}
                  </p>
                ) : mode === 'attr' ? (
                  snippetHtml ? (
                    <p className="search-hit-snippet">
                      命中属性：
                      <HtmlSnippet html={snippetHtml} />
                    </p>
                  ) : (
                    <p className="search-hit-snippet">
                      {h.snippet ? `命中属性：${h.snippet}` : '（无匹配属性）'}
                    </p>
                  )
                ) : snippetHtml ? (
                  <p className="search-hit-snippet">
                    <HtmlSnippet html={snippetHtml} />
                  </p>
                ) : (
                  <p className="search-hit-snippet">{h.snippet}</p>
                )}
              </article>
            )})}
            {showPagination ? (
              <div className="search-results-pagination">
                <Pagination
                  {...listPagination(page, resultTotal, (p, ps) => {
                    const nextSize = ps ?? pageSize
                    const nextPage = nextSize !== pageSize ? 1 : p
                    handlePaginationChange(p, ps)
                    if (res?.client_paginate) {
                      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                      return
                    }
                    runSearch(res!.query, nextPage, nextSize)
                  }, { unit: resultUnit, ...paginationOptions })}
                />
              </div>
            ) : null}
          </section>
        )}

        {!loading && !attrSearch && res && resultTotal === 0 && (
          <div className="search-empty-inline">
            {mode === 'vector'
              ? '未找到语义相近的文档，请换问法重试'
              : mode === 'attr'
                ? '未找到匹配属性的文档，请换属性关键词或确认已回填属性向量'
                : '未找到相关内容，请换关键词重试'}
          </div>
        )}

        {!loading && !attrSearch && res && resultTotal > 0 && visibleHits.length === 0 && (
          <div className="search-empty-inline">
            {tab === 'ai'
              ? '本页无结果，请尝试其他页码或更换关键词'
              : '当前分类下无匹配结果，可切换「AI搜索」查看全部结果'}
          </div>
        )}
      </div>

      <VectorSearchChunkDrawer
        open={vectorDrawerOpen}
        onClose={() => {
          setVectorDrawerOpen(false)
          setVectorDrawerHit(null)
        }}
        query={res?.query ?? ''}
        scope={scope}
        itemId={vectorDrawerHit?.item_id ?? null}
        docTitle={vectorDrawerHit?.title ?? ''}
        topScore={vectorDrawerHit?.score}
      />
    </div>
  )
}
